import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { ExportLcService } from '../../services/exportLc.service';
import { BondLicenseService } from '../../services/bondLicense.service';
import { ICreateUdRegisterRequest } from '../../interfaces/commercial.interface';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import BuyerOrderRefPicker from './common/BuyerOrderRefPicker';
import { fetchAllPages } from './common/fetchAllPages';

const EMPTY: ICreateUdRegisterRequest = {
    udNumber: '', udDate: '', exportLcId: null, salesOrderId: null, salesOrderRef: '',
    bondLicenseId: null, issuingAuthority: '', validTillDate: null, remarks: '',
};

/** Create/edit the UD register header (§5.1). No `status` field on create -- every UD is
 * created DRAFT (§5.3); status transitions live on UdRegisterDetail. */
const UdRegisterForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id && id !== 'new';
    const navigate = useNavigate();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [form, setForm] = useState<ICreateUdRegisterRequest>(EMPTY);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [exportLcOptions, setExportLcOptions] = useState<RefOption[]>([]);
    const [bondLicenseOptions, setBondLicenseOptions] = useState<RefOption[]>([]);
    const [refOptionsLoading, setRefOptionsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setRefOptionsLoading(true);
            try {
                const [exportLcs, bondLicenses] = await Promise.all([
                    fetchAllPages((page, size) => ExportLcService.list(institutionId, { page, size })),
                    fetchAllPages((page, size) => BondLicenseService.list(institutionId, page, size)),
                ]);
                if (cancelled) return;
                setExportLcOptions(exportLcs.map(lc => ({ id: lc.id, label: lc.lcNumber })));
                setBondLicenseOptions(bondLicenses.map(bl => ({ id: bl.id, label: bl.licenseNo })));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load export LC / bond licence options');
            } finally {
                if (!cancelled) setRefOptionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const ud = await UdRegisterService.get(institutionId, id!);
                setForm({
                    udNumber: ud.udNumber, udDate: ud.udDate, exportLcId: ud.exportLcId,
                    salesOrderId: ud.salesOrderId, salesOrderRef: ud.salesOrderRef ?? '',
                    bondLicenseId: ud.bondLicenseId,
                    issuingAuthority: ud.issuingAuthority, validTillDate: ud.validTillDate, remarks: ud.remarks ?? '',
                });
            } catch (e) {
                toast.showApiError(e, 'Failed to load UD register');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const valid = form.udNumber.trim().length > 0 && form.udNumber.length <= 100
        && !!form.udDate && form.issuingAuthority.trim().length > 0 && form.issuingAuthority.length <= 255;

    const saveWhy = saving ? null
        : form.udNumber.trim().length === 0 ? 'Enter the UD number.'
        : form.udNumber.length > 100 ? 'The UD number can be 100 characters at most.'
        : !form.udDate ? 'Enter the UD date.'
        : form.issuingAuthority.trim().length === 0 ? 'Enter the issuing authority.'
        : form.issuingAuthority.length > 255 ? 'The issuing authority can be 255 characters at most.'
        : null;

    const handleSave = async () => {
        // Submit trimmed values -- Save is gated on trimmed length, so an untrimmed value here
        // would silently persist leading/trailing whitespace the backend's @NotBlank never catches
        // (it checks trimmed length too).
        const payload: ICreateUdRegisterRequest = {
            ...form,
            udNumber: form.udNumber.trim(),
            issuingAuthority: form.issuingAuthority.trim(),
            salesOrderRef: form.salesOrderRef?.trim() || null,
            remarks: form.remarks?.trim() || null,
        };
        try {
            setSaving(true);
            if (isEdit) {
                await UdRegisterService.update(institutionId, id!, { ...payload });
                toast.showSuccess('UD register updated');
                navigate(`/commercial/ud-register/${id}`);
            } else {
                const created = await UdRegisterService.create(institutionId, payload);
                toast.showSuccess('UD register created');
                navigate(`/commercial/ud-register/${created.id}`);
            }
        } catch (e) {
            toast.showApiError(e, 'Failed to save UD register');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="commercial.udForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit UD Register' : 'New UD Register'}</Typography>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="commercial.udForm.section.details">
                <TextField data-help="commercial.udForm.field.udNumber" label="UD Number" value={form.udNumber} required
                    onChange={e => setForm({ ...form, udNumber: e.target.value })} />
                <TextField data-help="commercial.udForm.field.udDate" label="UD Date" type="date" InputLabelProps={{ shrink: true }} required
                    value={form.udDate} onChange={e => setForm({ ...form, udDate: e.target.value })} />
                <TextField data-help="commercial.udForm.field.issuingAuthority" label="Issuing Authority" value={form.issuingAuthority} required
                    onChange={e => setForm({ ...form, issuingAuthority: e.target.value })} />
                <BuyerOrderRefPicker
                    data-help="commercial.udForm.field.buyerOrder"
                    institutionId={institutionId}
                    label="Buyer Order (optional)"
                    value={form.salesOrderId ?? null}
                    valueLabel={form.salesOrderRef || null}
                    onChange={(orderId, order) => setForm(f => ({
                        ...f, salesOrderId: orderId, salesOrderRef: order?.orderNumber ?? '',
                    }))}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                        <RefAutocomplete
                            data-help="commercial.udForm.field.exportLc"
                            label="Export LC (optional)"
                            options={exportLcOptions}
                            loading={refOptionsLoading}
                            value={form.exportLcId ?? null}
                            onChange={newId => setForm(f => ({ ...f, exportLcId: newId }))}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                        <RefAutocomplete
                            data-help="commercial.udForm.field.bondLicense"
                            label="Bond Licence (optional)"
                            options={bondLicenseOptions}
                            loading={refOptionsLoading}
                            value={form.bondLicenseId ?? null}
                            onChange={newId => setForm(f => ({ ...f, bondLicenseId: newId }))}
                        />
                    </Box>
                </Box>
                <TextField data-help="commercial.udForm.field.validTill" label="Valid Till (optional)" type="date" InputLabelProps={{ shrink: true }}
                    value={form.validTillDate ?? ''} onChange={e => setForm({ ...form, validTillDate: e.target.value || null })} />
                <TextField data-help="commercial.udForm.field.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''}
                    onChange={e => setForm({ ...form, remarks: e.target.value })} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button data-help="commercial.udForm.cancel" onClick={() => navigate(-1)}>Cancel</Button>
                    <Button data-help="commercial.udForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default UdRegisterForm;
